import React from 'react';
import { Box, Container, Heading, Text, VStack, Link, Divider } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';

// Política de Privacidade — página PÚBLICA (sem auth). É a URL exigida pela
// verificação do Google (escopo do Drive) e referência de confiança do cliente.
const Privacy: React.FC = () => {
  const CONTATO = 'contato@aplicaiaapp.com';
  return (
    <Box minH="100vh" bg="gray.50" py={10}>
      <Container maxW="3xl" bg="white" p={{ base: 6, md: 10 }} borderRadius="lg" shadow="sm">
        <VStack align="stretch" spacing={5}>
          <Box>
            <Link as={RouterLink} to="/" color="brand.600" fontSize="sm">← Voltar</Link>
            <Heading size="lg" mt={3} color="brand.700">Política de Privacidade</Heading>
            <Text fontSize="sm" color="gray.500" mt={1}>Última atualização: 11 de junho de 2026</Text>
          </Box>

          <Text>
            Esta Política descreve como a <strong>Aplica IA</strong> ("nós") trata os dados ao
            fornecer uma plataforma que conecta as suas fontes (documentos, bancos de dados e
            aplicativos) e permite buscar e conversar com esse conteúdo usando inteligência
            artificial. Ao usar a plataforma, você concorda com esta Política.
          </Text>

          <Section title="1. Dados que tratamos">
            <Text>• <strong>Cadastro:</strong> nome, e-mail e dados da organização para criar e administrar a sua conta.</Text>
            <Text>• <strong>Conteúdo conectado:</strong> os documentos e registros das fontes que <em>você</em> escolhe conectar (ex.: arquivos do Google Drive, planilhas, registros de banco de dados). Acessamos apenas o que você autoriza, em modo <strong>somente-leitura</strong>.</Text>
            <Text>• <strong>Uso:</strong> registros técnicos de execução (logs) para operar e dar suporte ao serviço.</Text>
          </Section>

          <Section title="2. Como usamos os dados">
            <Text>Usamos o conteúdo conectado <strong>exclusivamente</strong> para prestar o serviço a você: indexar, buscar e gerar respostas a partir das suas próprias fontes. Não usamos seu conteúdo para publicidade e não o vendemos.</Text>
          </Section>

          <Section title="3. Uso de dados das APIs do Google (Limited Use)">
            <Text>
              O uso e a transferência, pela Aplica IA, de informações recebidas das APIs do Google
              obedecem à <strong>Política de Dados do Usuário dos Serviços de API do Google</strong>,
              incluindo os requisitos de <strong>Uso Limitado (Limited Use)</strong>. Em particular:
            </Text>
            <Text>• acessamos seus arquivos do Google Drive em <strong>somente-leitura</strong>, apenas no escopo que você selecionar;</Text>
            <Text>• usamos esses dados <strong>somente</strong> para fornecer e melhorar as funcionalidades visíveis a você (busca e respostas com IA sobre o seu conteúdo);</Text>
            <Text>• <strong>não</strong> transferimos esses dados a terceiros, exceto o necessário para operar o serviço, cumprir a lei ou com o seu consentimento;</Text>
            <Text>• <strong>não</strong> usamos esses dados para publicidade e <strong>não</strong> permitimos que humanos os leiam, salvo com o seu consentimento, para suporte/segurança ou quando exigido por lei.</Text>
          </Section>

          <Section title="4. Compartilhamento com provedores">
            <Text>Para gerar respostas, trechos do seu conteúdo podem ser enviados a provedores de modelos de IA (ex.: OpenAI/OpenRouter) estritamente para processar a sua solicitação. Esses provedores atuam como operadores, sob contrato, e não usam o conteúdo para treinar modelos próprios quando aplicável às contas corporativas.</Text>
          </Section>

          <Section title="5. Segurança e isolamento">
            <Text>Credenciais e tokens ficam <strong>criptografados em repouso</strong>. Cada organização (tenant) só acessa os próprios dados. As conexões são por TLS. Para bancos internos, oferecemos um Agente que envia dados por conexão de saída, sem expor o banco.</Text>
          </Section>

          <Section title="6. Retenção e exclusão">
            <Text>Você pode <strong>desconectar uma fonte</strong> ou <strong>excluir uma base</strong> a qualquer momento; os dados indexados correspondentes são removidos. Ao encerrar a conta, removemos seus dados, salvo retenção exigida por lei.</Text>
          </Section>

          <Section title="7. Seus direitos (LGPD)">
            <Text>Você pode solicitar acesso, correção, portabilidade ou exclusão dos seus dados, além de revogar consentimentos. Para exercer, escreva para <Link href={`mailto:${CONTATO}`} color="brand.600">{CONTATO}</Link>.</Text>
          </Section>

          <Divider />
          <Section title="8. Contato">
            <Text>Dúvidas sobre esta Política: <Link href={`mailto:${CONTATO}`} color="brand.600">{CONTATO}</Link>.</Text>
          </Section>

          <Text fontSize="sm" color="gray.500">
            Veja também os nossos <Link as={RouterLink} to="/termos" color="brand.600">Termos de Serviço</Link>.
          </Text>
        </VStack>
      </Container>
    </Box>
  );
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box>
    <Heading size="sm" mb={2} color="gray.700">{title}</Heading>
    <VStack align="stretch" spacing={1} color="gray.700" fontSize="sm">
      {children}
    </VStack>
  </Box>
);

export default Privacy;
